export enum FileStatus {
	PENDING = 'pending',
	CONFIRMED = 'confirmed',
	FAILED = 'failed',
}

export enum FileSharePermission {
	VIEW = 'view',
	DOWNLOAD = 'download',
}

export enum FileCategory {
	DOCUMENT = 'document',
	IMAGE = 'image',
	SPREADSHEET = 'spreadsheet',
	PRESENTATION = 'presentation',
	AUDIO = 'audio',
	VIDEO = 'video',
	ARCHIVE = 'archive',
	OTHER = 'other',
}

export enum FileProcessingStatus {
	PROCESSING = 'processing',
	COMPLETED = 'completed',
	FAILED = 'failed',
}

/**
 * Which of the two summary/description generation lanes actually wrote a file's fields.
 * `READER` — a remote reader had already described the asset. `MODEL` — one generation call
 * over the file's extracted text.
 */
export enum FileSummaryLane {
	READER = 'reader',
	MODEL = 'model',
}

export enum FileProcessingResultStatus {
	PENDING = 'pending',
	RUNNING = 'running',
	COMPLETED = 'completed',
	FAILED = 'failed',
	/**
	 * Never ran, and never will on this pass: a dependency failed, or the
	 * dependency graph could not be resolved. Terminal, and distinct from FAILED
	 * — the concern itself did not fail, so reporting it as a failure would put
	 * an error on a thumbnail that was simply never attempted.
	 *
	 * Terminal matters more than the label: without a persisted terminal status a
	 * skipped concern's row stayed `pending` forever, so the file never settled
	 * for the client and the row was invisible to the stall sweep.
	 */
	SKIPPED = 'skipped',
}

/**
 * The post-upload concerns in `FILE_PROCESSING_CONCERNS`.
 *
 * An enum rather than bare strings because these ids cross service boundaries:
 * `file-service` writes a row keyed by one, the worker's dispatcher matches on
 * it, and the frontend keys its follow-up off it. A typo or a rename in any one
 * of those places produced no error — just a concern that silently never ran.
 */
export enum FileProcessingConcernId {
	VIRUS_SCAN = 'virus_scan',
	THUMBNAIL_GENERATION = 'thumbnail_generation',
	AUDIO_TRANSCRIPTION = 'audio_transcription',
	TEXT_EXTRACTION = 'text_extraction',
	TEXT_EXTRACTION_ESCALATED = 'text_extraction_escalated',
	MEDIA_UNDERSTANDING = 'media_understanding',
	VIDEO_UNDERSTANDING = 'video_understanding',
	CLASSIFICATION = 'classification',
}

/**
 * Lifecycle of a multipart upload session. `ACTIVE`/`ABORTING`/`COMPLETING`/`COMPLETED`
 * are in-flight (at most one per File, enforced by a partial unique index); the rest are
 * terminal.
 */
export enum MultipartSessionStatus {
	ACTIVE = 'ACTIVE',
	ABORTING = 'ABORTING',
	COMPLETING = 'COMPLETING',
	COMPLETED = 'COMPLETED',
	CONFIRMED = 'CONFIRMED',
	ABORTED = 'ABORTED',
	EXPIRED = 'EXPIRED',
	FAILED = 'FAILED',
}

/**
 * Outcome of reconciling a session's stored status against live S3 state
 * (`classifySession`). `COMPLETING`/`COMPLETED`-derived verdicts are unreachable
 * until a later slice adds `POST /complete`, but are defined now since the
 * classifier is one shared pure function every slice imports.
 */
export enum MultipartReconciliationVerdict {
	RESUME_PARTS = 'RESUME_PARTS',
	EXPIRE = 'EXPIRE',
	FINALIZATION_IN_PROGRESS = 'FINALIZATION_IN_PROGRESS',
	RETRY_COMPLETION = 'RETRY_COMPLETION',
	RETRY_VALIDATION = 'RETRY_VALIDATION',
	FINALIZE_FAILED = 'FINALIZE_FAILED',
	RETRY_ABORT = 'RETRY_ABORT',
	ABORT_CONFIRMED = 'ABORT_CONFIRMED',
	NONE = 'NONE',
}

/**
 * Why the client is abandoning a multipart session, sent as `DELETE
 * /files/{id}/multipart`'s `reason` query parameter. Diagnostic only — it
 * never changes authorization or abort semantics.
 */
export enum MultipartAbortReason {
	LOCAL_SIZE_MISMATCH = 'local_size_mismatch',
}

/** Which way the client's local file disagreed with the trusted declared size. */
export enum MultipartLocalSizeMismatchDirection {
	LOCAL_GT_DECLARED = 'local_gt_declared',
	LOCAL_LT_DECLARED = 'local_lt_declared',
}

/** The failure classes a multipart upload can end in that the frontend UI has distinct behavior for. */
export enum MultipartFailureKind {
	/** The upload can no longer be resumed or finalized; the user must start over. */
	RESTART_REQUIRED = 'restart_required',
	/** Still recoverable — the same session survives and the user may retry. */
	RETRYABLE = 'retryable',
	/** The user cancelled. */
	CANCELLED = 'cancelled',
	/** The selected local file is not the one this session was created for. */
	FILE_MISMATCH = 'file_mismatch',
}

/**
 * Why a multipart attempt handed the upload back to single PUT. Internal
 * frontend diagnostic only — logged, never sent to the server and never shown
 * to the user in this form.
 */
export enum MultipartFallbackReason {
	PUBLIC_FILE_NOT_SUPPORTED = 'public_file_not_supported',
	DECLARED_SIZE_REQUIRED = 'declared_size_required',
	LOCAL_SIZE_MISMATCH = 'local_size_mismatch',
}

/**
 * Where a font face's family name came from.
 *
 * A face grouped from its own `name` table is the font's own answer. A face
 * grouped from its filename is a guess, and the two must stay distinguishable:
 * a guess can be corrected later, and only if the rows that were guesses can be
 * found.
 */
export enum FontFamilyNameSource {
	/** Read from the face's sfnt `name` table. */
	METADATA = 'metadata',
	/** Derived from the filename because the `name` table could not be read. */
	FILENAME = 'filename',
}
